import { Button, Group, Image, Loader, Modal, Stack, Text, Textarea, TextInput, UnstyledButton } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

import { BUG_REPORT_EXCLUDE_ATTR, jpegBase64ToDataUrl } from '@/lib/captureViewportScreenshot';
import { BugReportAnnotateModal } from './BugReportAnnotateModal';

export interface BugReportModalProps {
  opened: boolean;
  capturing: boolean;
  screenshotBase64: string | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: { summary: string; details: string; screenshotBase64: string }) => void;
  onScreenshotChange: (jpegBase64: string) => void;
}

export function BugReportModal({
  opened,
  capturing,
  screenshotBase64,
  submitting,
  onClose,
  onSubmit,
  onScreenshotChange,
}: BugReportModalProps): JSX.Element {
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [annotateOpened, setAnnotateOpened] = useState(false);

  // Render modal into an excluded portal so the capture never includes it
  const portalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const div = document.createElement('div');
    div.setAttribute(BUG_REPORT_EXCLUDE_ATTR, '');
    document.body.appendChild(div);
    portalRef.current = div;
    return () => {
      div.parentNode?.removeChild(div);
      portalRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!opened) {
      setSummary('');
      setDetails('');
      setAnnotateOpened(false);
    }
  }, [opened]);

  const canSubmit = Boolean(screenshotBase64 && summary.trim() && details.trim() && !submitting);
  const screenshotUrl = screenshotBase64 ? jpegBase64ToDataUrl(screenshotBase64) : '';

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title="Report a bug"
        size="xl"
        centered
        portalProps={{ target: portalRef.current ?? undefined }}
        closeOnClickOutside={!submitting}
        closeOnEscape={!submitting && !annotateOpened}
        withCloseButton={!submitting}
        trapFocus={!annotateOpened}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This report includes what is visible on your screen and may include client information.
            Click the screenshot to highlight the issue.
          </Text>
          {capturing && (
            <Stack
              align="center"
              justify="center"
              h={180}
              bd="1px solid var(--mantine-color-gray-4)"
              style={{ borderRadius: 'var(--mantine-radius-sm)' }}
            >
              <Loader size="md" />
              <Text size="sm" c="dimmed">Capturing screenshot…</Text>
            </Stack>
          )}
          {!capturing && screenshotUrl && (
            <UnstyledButton
              aria-label="Annotate screenshot"
              onClick={() => setAnnotateOpened(true)}
              disabled={submitting}
              w="100%"
              bd="1px solid var(--mantine-color-gray-4)"
            >
              <Image src={screenshotUrl} alt="" maw="100%" mah={280} fit="contain" />
            </UnstyledButton>
          )}
          <TextInput
            label="Summary"
            placeholder="One-line description of the bug"
            required
            value={summary}
            onChange={(event) => setSummary(event.currentTarget.value)}
            maxLength={200}
            disabled={submitting}
          />
          <Textarea
            label="Description"
            placeholder='Describe the issue in as much detail as possible to help us understand and fix the problem.'
            required
            rows={4}
            autosize={false}
            value={details}
            onChange={(event) => setDetails(event.currentTarget.value)}
            maxLength={8000}
            disabled={submitting}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              loading={submitting}
              disabled={!canSubmit}
              onClick={() => {
                if (!screenshotBase64) return;
                onSubmit({
                  summary: summary.trim(),
                  details: details.trim(),
                  screenshotBase64,
                });
              }}
            >
              Submit
            </Button>
          </Group>
        </Stack>
      </Modal>
      {screenshotUrl && (
        <BugReportAnnotateModal
          opened={annotateOpened}
          screenshotDataUrl={screenshotUrl}
          onCancel={() => setAnnotateOpened(false)}
          onDone={(next) => {
            onScreenshotChange(next);
            setAnnotateOpened(false);
          }}
        />
      )}
    </>
  );
}
